import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, query, orderBy, where, updateDoc, getDoc } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Propiedad {
  docId: string;  // UID de Firestore (para enlaces y operaciones)
  id?: string;  // ID personalizado (propiedad-35625)
  propiedad_id?: string;
  list_number?: string;
  titulo?: string;
  nombre?: string;
  precio_actual_usd?: number;
  precio_original_usd?: number;
  precio_actual_mxn?: number;
  estado?: string;
  estatus?: string;
  activo?: boolean;
  inmobiliaria_doc_id?: string; // ✅ doc.id de Firestore
  agencia_nombre?: string; // ✅ Nombre cargado dinámicamente
  asesor_doc_ids?: string[]; // ✅ Array de doc.ids
  fecha_creacion?: any;
  [key: string]: any;
}

export default function AdminPropiedades() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filtroAsesorUid = searchParams.get('asesorUid'); // UID del asesor desde URL
  const [asesorInfo, setAsesorInfo] = useState<any>(null); // Info del asesor filtrado
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [todasLasPropiedades, setTodasLasPropiedades] = useState<Propiedad[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroAgencia, setFiltroAgencia] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(true);
  const propiedadesPorPagina = 10;

  useEffect(() => {
    const fetchPropiedades = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Si hay filtro por asesor UID, cargar info del asesor primero
        if (filtroAsesorUid) {
          try {
            const asesorDocRef = doc(db, 'asesor', filtroAsesorUid);
            const asesorDocSnap = await getDoc(asesorDocRef);

            if (asesorDocSnap.exists()) {
              const asesorData = asesorDocSnap.data();
              setAsesorInfo({
                id: asesorDocSnap.id,
                nombre: asesorData.nombre,
                asesor_id: asesorData.asesor_id
              });
            } else {
              console.warn('Asesor no encontrado con UID:', filtroAsesorUid);
              setAsesorInfo(null);
            }
          } catch (err) {
            console.error('Error cargando asesor:', err);
            setAsesorInfo(null);
          }
        } else {
          setAsesorInfo(null);
        }

        // Cargar TODAS las propiedades para admin
        const propiedadesRef = collection(db, 'propiedades');
        // ✅ Removemos orderBy para evitar errores con propiedades sin fecha_creacion
        const snapshot = await getDocs(propiedadesRef);

        const todasPropiedades = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();

            // ✅ Cargar nombre de inmobiliaria usando inmobiliaria_doc_id
            let nombreInmobiliaria = 'Sin agencia';
            if (data.inmobiliaria_doc_id) {
              try {
                const inmoDocRef = doc(db, 'inmobiliaria', data.inmobiliaria_doc_id);
                const inmoSnap = await getDoc(inmoDocRef);
                if (inmoSnap.exists()) {
                  nombreInmobiliaria = inmoSnap.data().nombre || 'Sin nombre';
                }
              } catch (error) {
                console.error('Error cargando inmobiliaria:', error);
              }
            }

            return {
              docId: docSnap.id,  // UID de Firestore
              ...data,
              agencia_nombre: nombreInmobiliaria // ✅ Agregar nombre de agencia
            } as Propiedad;
          })
        );

        // ✅ Ordenamos en el cliente por fecha_creacion (más recientes primero)
        todasPropiedades.sort((a, b) => {
          const fechaA = a.fecha_creacion?.seconds || 0;
          const fechaB = b.fecha_creacion?.seconds || 0;
          return fechaB - fechaA; // Descendente (más recientes primero)
        });

        // Filtrar por permisos de usuario
        const esAdmin = user.tipo?.includes('admin');
        let propiedadesFiltradas: Propiedad[];

        if (esAdmin) {
          // Admin ve todas las propiedades
          propiedadesFiltradas = todasPropiedades;
        } else {
          // No-admin ve solo sus propiedades
          // ✅ CORREGIDO: Usar inmobiliaria_doc_id del usuario
          propiedadesFiltradas = todasPropiedades.filter((p: Propiedad) => {
            return p.inmobiliaria_doc_id === user.inmobiliaria_doc_id;
          });
        }

        setTodasLasPropiedades(propiedadesFiltradas);
        setPropiedades(propiedadesFiltradas);

      } catch (error) {
        console.error('Error cargando propiedades:', error);
        Swal.fire('Error', 'No se pudieron cargar las propiedades.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPropiedades();
  }, [user, filtroAsesorUid]);

  // Filtrar propiedades basado en los filtros aplicados
  useEffect(() => {
    let propiedadesFiltradas = todasLasPropiedades;

    // Filtro por asesor UID (desde URL)
    if (filtroAsesorUid && asesorInfo) {
      propiedadesFiltradas = propiedadesFiltradas.filter(p => {
        // ✅ CORREGIDO: Usar asesor_doc_ids (array de doc.ids)
        const asesorDocIds = p.asesor_doc_ids || [];
        // Buscar por doc.id del asesor
        return Array.isArray(asesorDocIds) && asesorDocIds.includes(asesorInfo.id);
      });
    }

    // Filtro por texto
    if (filtro.trim() !== '') {
      propiedadesFiltradas = propiedadesFiltradas.filter(p => {
        const nombre = p.titulo || p.nombre || `Propiedad ${p.list_number || 'Sin título'}`;
        return nombre.toLowerCase().includes(filtro.toLowerCase());
      });
    }

    // Filtro por agencia
    if (filtroAgencia.trim() !== '') {
      propiedadesFiltradas = propiedadesFiltradas.filter(p => {
        const agencia = p.agencia_nombre || '';
        return agencia.toLowerCase().includes(filtroAgencia.toLowerCase());
      });
    }

    // Filtro por estatus
    if (filtroEstatus !== '') {
      propiedadesFiltradas = propiedadesFiltradas.filter(p => {
        const estatus = p.estado || p.estatus || 'Sin definir';
        return estatus === filtroEstatus;
      });
    }

    setPropiedades(propiedadesFiltradas);
    setPaginaActual(1); // Reset a la primera página
  }, [filtro, filtroEstatus, filtroAgencia, todasLasPropiedades, filtroAsesorUid, asesorInfo]);

  if (!user || (!user.tipo?.includes('admin') && !user.tipo?.includes('asociado'))) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando propiedades...</span>
          </div>
          <p className="mt-3">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  const handleDelete = async (propiedad: Propiedad) => {
    const nombrePropiedad = propiedad.titulo || propiedad.nombre || `Propiedad ${propiedad.list_number}`;
    
    const confirm = await Swal.fire({
      title: '¿Eliminar propiedad?',
      text: `Se eliminará "${nombrePropiedad}" permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteDoc(doc(db, 'propiedades', propiedad.docId));

      // Actualizar estado local
      const nuevasPropiedades = todasLasPropiedades.filter(p => p.docId !== propiedad.docId);
      setTodasLasPropiedades(nuevasPropiedades);
      
      Swal.fire('Eliminada', 'La propiedad ha sido eliminada correctamente.', 'success');
    } catch (error) {
      console.error('Error al eliminar propiedad:', error);
      Swal.fire('Error', 'No se pudo eliminar la propiedad.', 'error');
    }
  };

  const handleToggleActivo = async (propiedad: Propiedad) => {
    const nuevoEstado = !propiedad.activo;
    
    try {
      await updateDoc(doc(db, 'propiedades', propiedad.docId), {
        activo: nuevoEstado
      });

      // Actualizar estado local
      const propiedadesActualizadas = todasLasPropiedades.map(p =>
        p.docId === propiedad.docId ? { ...p, activo: nuevoEstado } : p
      );
      setTodasLasPropiedades(propiedadesActualizadas);
      
      Swal.fire(
        'Actualizado',
        `La propiedad ha sido ${nuevoEstado ? 'activada' : 'desactivada'}.`,
        'success'
      );
    } catch (error) {
      console.error('Error actualizando propiedad:', error);
      Swal.fire('Error', 'No se pudo actualizar la propiedad.', 'error');
    }
  };

  const formatPrice = (propiedad: Propiedad) => {
    // Priorizar USD, luego MXN
    if (propiedad.precio_actual_usd && propiedad.precio_actual_usd > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(propiedad.precio_actual_usd);
    } else if (propiedad.precio_original_usd && propiedad.precio_original_usd > 0) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(propiedad.precio_original_usd);
    } else if (propiedad.precio_actual_mxn && propiedad.precio_actual_mxn > 0) {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0
      }).format(propiedad.precio_actual_mxn);
    }
    return 'No definido';
  };

  const getEstatus = (propiedad: Propiedad) => {
    return propiedad.estado || propiedad.estatus || 'Sin definir';
  };

  const getEstatusColor = (estatus: string) => {
    switch (estatus.toLowerCase()) {
      case 'a':
      case 'activo':
      case 'disponible':
        return 'success';
      case 'vendida':
      case 'vendido':
        return 'danger';
      case 'pendiente':
      case 'pending':
        return 'warning';
      case 'inactivo':
      case 'pausado':
        return 'secondary';
      default:
        return 'info';
    }
  };

  // Paginación
  const totalPaginas = Math.ceil(propiedades.length / propiedadesPorPagina);
  const startIndex = (paginaActual - 1) * propiedadesPorPagina;
  const propiedadesPaginadas = propiedades.slice(startIndex, startIndex + propiedadesPorPagina);

  // Obtener estatuses únicos para el filtro
  const estatusUnicos = Array.from(new Set(
    todasLasPropiedades.map(p => p.estado || p.estatus || 'Sin definir')
  )).sort();

  // Obtener agencias únicas para el filtro
  const agenciasUnicas = Array.from(new Set(
    todasLasPropiedades
      .map(p => p.agencia_nombre)
      .filter(Boolean)
  )).sort();

  return (
    <>
      <UserNav />
      <div className="container mt-4">
        <Breadcrumb
          items={
            filtroAsesorUid && asesorInfo
              ? [
                  { label: 'Dashboard', path: '/admin-dashboard' },
                  { label: 'Administración de Afiliados', path: '/admin-afiliado' },
                  { label: asesorInfo.nombre, path: `/ver-admin-afiliado/${filtroAsesorUid}` },
                  { label: 'Propiedades' }
                ]
              : [
                  { label: 'Dashboard', path: '/admin-dashboard' },
                  { label: 'Administración de Propiedades' }
                ]
          }
        />
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="mb-0">
              <i className="fas fa-home me-2 text-primary"></i>
              {filtroAsesorUid && asesorInfo
                ? `Propiedades de ${asesorInfo.nombre}`
                : 'Administración de Propiedades'}
            </h1>
            {filtroAsesorUid && asesorInfo && (
              <div className="mt-2">
                <span className="badge bg-info">
                  <i className="fas fa-filter me-1"></i>
                  Filtrado por asesor: {asesorInfo.nombre}
                </span>
              </div>
            )}
          </div>
          {user.tipo?.includes('admin') && !filtroAsesorUid && (
            <button
              className="btn btn-success"
              onClick={() => navigate('/submit-property')}
            >
              <i className="fa fa-plus me-2"></i>Nueva Propiedad
            </button>
          )}
        </div>

        {/* Estadísticas Resumen */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center bg-primary text-white">
              <div className="card-body">
                <h3>{todasLasPropiedades.length}</h3>
                <p className="mb-0">Total Propiedades</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-success text-white">
              <div className="card-body">
                <h3>{todasLasPropiedades.filter(p => p.activo !== false).length}</h3>
                <p className="mb-0">Activas</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-warning text-white">
              <div className="card-body">
                <h3>{todasLasPropiedades.filter(p => p.activo === false).length}</h3>
                <p className="mb-0">Inactivas</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center bg-info text-white">
              <div className="card-body">
                <h3>{propiedades.length}</h3>
                <p className="mb-0">Filtradas</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Buscar por nombre:</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar propiedades..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Filtrar por agencia:</label>
                <select
                  className="form-select"
                  value={filtroAgencia}
                  onChange={(e) => setFiltroAgencia(e.target.value)}
                >
                  <option value="">Todas las agencias</option>
                  {agenciasUnicas.map(agencia => (
                    <option key={agencia} value={agencia}>{agencia}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Filtrar por estatus:</label>
                <select
                  className="form-select"
                  value={filtroEstatus}
                  onChange={(e) => setFiltroEstatus(e.target.value)}
                >
                  <option value="">Todos los estatus</option>
                  {estatusUnicos.map(estatus => (
                    <option key={estatus} value={estatus}>{estatus}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setFiltro('');
                    setFiltroEstatus('');
                    setFiltroAgencia('');
                  }}
                >
                  <i className="fas fa-times me-1"></i>Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de Propiedades */}
        {propiedades.length === 0 ? (
          <div className="alert alert-info text-center">
            <i className="fas fa-info-circle me-2"></i>
            {filtroAsesorUid && asesorInfo
              ? `${asesorInfo.nombre} no tiene propiedades asignadas actualmente.`
              : 'No hay propiedades que coincidan con los filtros aplicados.'}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="table-dark">
                  <tr>
                    <th>Nombre</th>
                    <th>Agencia</th>
                    <th>Precio</th>
                    <th>Estatus</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {propiedadesPaginadas.map(propiedad => {
                    const nombre = propiedad.titulo || propiedad.nombre || `Propiedad ${propiedad.list_number || 'Sin título'}`;
                    const agencia = propiedad.agencia_nombre || 'Sin agencia';
                    const estatus = getEstatus(propiedad);

                    return (
                      <tr key={propiedad.docId}>
                        <td>
                          <div className="fw-bold">{nombre}</div>
                          <small className="text-muted">
                            <i className="fas fa-hashtag me-1"></i>
                            {propiedad.propiedad_id || propiedad.list_number || 'Sin ID'}
                          </small>
                        </td>
                        <td>
                          <i className="fas fa-building me-1 text-muted"></i>
                          {agencia}
                        </td>
                        <td className="fw-bold text-success">{formatPrice(propiedad)}</td>
                        <td>
                          <span className={`badge bg-${getEstatusColor(estatus)}`}>
                            {estatus}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${propiedad.activo !== false ? 'bg-success' : 'bg-secondary'}`}>
                            {propiedad.activo !== false ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            {/* Ver */}
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => navigate(`/single-property-1/${propiedad.docId}`)}
                              title="Ver propiedad"
                            >
                              <i className="fas fa-eye"></i>
                            </button>

                            {/* Ver Admin */}
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(`/view-admin-property/${propiedad.docId}`)}
                              title="Vista administrativa"
                            >
                              <i className="fas fa-cog"></i>
                            </button>

                            {/* Editar */}
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => navigate(`/edit-property/${propiedad.docId}`)}
                              title="Editar propiedad"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            
                            {/* Toggle Activo/Inactivo */}
                            <button
                              className={`btn btn-sm ${propiedad.activo !== false ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                              onClick={() => handleToggleActivo(propiedad)}
                              title={propiedad.activo !== false ? 'Desactivar' : 'Activar'}
                            >
                              <i className={`fas ${propiedad.activo !== false ? 'fa-pause' : 'fa-play'}`}></i>
                            </button>
                            
                            {/* Eliminar */}
                            {user.tipo?.includes('admin') && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(propiedad)}
                                title="Eliminar propiedad"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <nav className="d-flex justify-content-center mt-4" aria-label="Paginación de propiedades">
                <ul className="pagination flex-wrap">
                  <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual - 1)}
                      disabled={paginaActual === 1}
                    >
                      <i className="fas fa-chevron-left me-1"></i>Anterior
                    </button>
                  </li>

                  {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                    let pageNum: number;
                    if (totalPaginas <= 5) {
                      pageNum = i + 1;
                    } else if (paginaActual <= 3) {
                      pageNum = i + 1;
                    } else if (paginaActual >= totalPaginas - 2) {
                      pageNum = totalPaginas - 4 + i;
                    } else {
                      pageNum = paginaActual - 2 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPaginaActual(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                    >
                      Siguiente<i className="fas fa-chevron-right ms-1"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            {/* Información de paginación */}
            <div className="text-center text-muted">
              Mostrando {startIndex + 1} a {Math.min(startIndex + propiedadesPorPagina, propiedades.length)} de {propiedades.length} propiedades
            </div>
          </>
        )}
      </div>
      
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}